import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, 
  Check, 
  Shield,
  Users,
  Package,
  FileText,
  CreditCard,
  Zap,
  Calendar,
  Rocket,
  HelpCircle,
  AlertTriangle,
  Lock
} from 'lucide-react';
import api from '../utils/api';

const SetupGuideWidget = ({ onNavigate, isVisible, onToggleVisibility, onOpenSettings }) => {
  const [isMinimized, setIsMinimized] = useState(true); // Start minimized to prevent flash
  const [hasCheckedCompletion, setHasCheckedCompletion] = useState(false); // Track if we've done initial check
  const [completionData, setCompletionData] = useState({
    hasRole: false,
    hasTeamMember: false,
    hasAsset: false,
    hasWaiverText: false,
    hasStripeConnect: false,
    hasService: false,
    hasBooking: false
  });
  const [loading, setLoading] = useState(true);

  // Fetch completion status
  useEffect(() => {
    const fetchCompletionStatus = async () => {
      try {
        // Fetch all data in parallel
        const [rolesRes, teamRes, assetsRes, servicesRes, bookingsRes, orgRes] = await Promise.all([
          api.get('/roles').catch(() => ({ data: [] })),
          api.get('/team').catch(() => ({ data: [] })),
          api.get('/assets').catch(() => ({ data: [] })),
          api.get('/services').catch(() => ({ data: [] })),
          api.get('/bookings').catch(() => ({ data: [] })),
          api.get('/organizations/me').catch(() => ({ data: {} }))
        ]);

        const org = orgRes.data;
        const teamData = Array.isArray(teamRes.data) ? teamRes.data : [];
        const bookingsData = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];
        
        // Check if waiver is configured (has text or is explicitly enabled)
        // Field is stored as customWaiverText in the org
        const waiverText = org.customWaiverText || org.waiverText || '';
        const hasWaiverText = waiverText.trim().length >= 50 || org.waiversEnabled === true;
        
        setCompletionData({
          hasRole: (rolesRes.data?.length || 0) > 0,
          hasTeamMember: teamData.length > 0,
          hasAsset: (assetsRes.data?.length || 0) > 0,
          hasWaiverText: hasWaiverText,
          hasStripeConnect: !!(org.stripeConnectAccountId && org.stripeConnectChargesEnabled),
          hasService: (servicesRes.data?.length || 0) > 0,
          hasBooking: bookingsData.length > 0
        });
      } catch (error) {
        console.error('Error fetching setup status:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isVisible) {
      fetchCompletionStatus();
      // Refresh every 30 seconds while visible
      const interval = setInterval(fetchCompletionStatus, 30000);
      
      // Listen for real-time updates when data changes
      const handleDataChange = () => {
        // Debug logging removed for production
        // console.log('Setup data changed, refreshing...');
        fetchCompletionStatus();
      };
      window.addEventListener('setupDataChanged', handleDataChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('setupDataChanged', handleDataChange);
      };
    }
  }, [isVisible]);

  // Auto-expand when there are incomplete steps (only on first check)
  useEffect(() => {
    if (loading || hasCheckedCompletion) return;
    
    const allComplete = completionData.hasRole && 
      completionData.hasTeamMember && 
      completionData.hasAsset && 
      completionData.hasWaiverText && 
      completionData.hasStripeConnect && 
      completionData.hasService && 
      completionData.hasBooking;
    
    // If NOT all complete, expand the guide to show user what's missing
    // If all complete, stay minimized
    if (!allComplete) {
      setIsMinimized(false);
    }
    setHasCheckedCompletion(true);
  }, [completionData, loading, hasCheckedCompletion]);

  // Check if business-critical requirements are met
  const canCreateServices = completionData.hasWaiverText && completionData.hasStripeConnect;
  const canCreateBookings = completionData.hasWaiverText && completionData.hasStripeConnect;

  const steps = [
    {
      id: 1,
      title: 'Create a Role',
      description: 'Define team positions (Captain, Guide, etc.)',
      icon: Shield,
      color: 'bg-blue-500',
      isComplete: completionData.hasRole,
      navigateTo: 'roles'
    },
    {
      id: 2,
      title: 'Add Team Member',
      description: 'Add yourself or staff with schedules',
      icon: Users,
      color: 'bg-indigo-500',
      isComplete: completionData.hasTeamMember,
      navigateTo: 'teamMembers'
    },
    {
      id: 3,
      title: 'Register an Asset',
      description: 'Add your resources (boats, venues, etc.)',
      icon: Package,
      color: 'bg-green-500',
      isComplete: completionData.hasAsset,
      navigateTo: 'assets'
    },
    {
      id: 4,
      title: 'Set Up Waiver',
      description: 'Required for liability protection',
      icon: FileText,
      color: 'bg-amber-500',
      isComplete: completionData.hasWaiverText,
      navigateTo: 'waivers',
      isCritical: true,
      criticalLabel: 'Required'
    },
    {
      id: 5,
      title: 'Connect Stripe',
      description: 'Required to accept payments',
      icon: CreditCard,
      color: 'bg-purple-500',
      isComplete: completionData.hasStripeConnect,
      navigateTo: 'payments',
      isCritical: true,
      criticalLabel: 'Required'
    },
    {
      id: 6,
      title: 'Create a Service',
      description: 'Build bookable services with pricing',
      icon: Zap,
      color: 'bg-pink-500',
      isComplete: completionData.hasService,
      navigateTo: 'services',
      // Only show as gated if NOT complete AND prerequisites missing
      isGated: !completionData.hasService && !canCreateServices,
      gateMessage: 'Complete Waiver & Stripe first'
    },
    {
      id: 7,
      title: 'Test Internal Booking',
      description: 'Create a test booking manually',
      icon: Calendar,
      color: 'bg-orange-500',
      isComplete: completionData.hasBooking,
      navigateTo: 'schedule',
      // Only show as gated if NOT complete AND prerequisites missing
      isGated: !completionData.hasBooking && !canCreateBookings,
      gateMessage: 'Complete Waiver & Stripe first'
    },
    {
      id: 8,
      title: "You're Live! 🎉",
      description: 'Start accepting customer bookings',
      icon: Rocket,
      color: 'bg-emerald-500',
      isComplete: completionData.hasWaiverText && completionData.hasStripeConnect && completionData.hasService,
      isFinalStep: true
    }
  ];

  // Count completed steps (excluding final "You're Live" step)
  // Steps 1-7 are actionable, step 8 is the celebration step
  const actionableSteps = steps.filter(s => !s.isFinalStep);
  const completedSteps = actionableSteps.filter(s => s.isComplete === true).length;
  const totalSteps = actionableSteps.length; // Should be 7
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  const handleStepClick = (step) => {
    if (step.isFinalStep) return;
    
    // If gated, show message but still allow navigation to see the gate
    if (onNavigate && step.navigateTo) {
      onNavigate(step.navigateTo);
    }
  };

  const handleHideGuide = () => {
    // Get org-specific key from token
    const token = localStorage.getItem('token');
    let key = 'hideSetupGuide';
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        key = `hideSetupGuide_${payload.organizationId || 'default'}`;
      } catch (e) {
        // Token parsing failed, use default key
      }
    }
    localStorage.setItem(key, 'true');
    onToggleVisibility(false);
    // Dispatch event so DashboardV2 can update
    window.dispatchEvent(new Event('guideVisibilityChanged'));
  };

  // Always show - either minimized or full panel (no permanent hide)
  // Minimized state - just show icon with hover background like other action icons
  if (isMinimized || !isVisible) {
    const handleOpenGuide = () => {
      setIsMinimized(false);
      // Also notify parent to show guide
      if (!isVisible) {
        onToggleVisibility(true);
      }
    };
    
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleOpenGuide}
          className="relative p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-all shadow-sm hover:shadow-md cursor-pointer"
          title="Open Setup Guide"
        >
          <HelpCircle className="w-6 h-6" />
          {completedSteps < totalSteps && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 bg-orange-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
              {totalSteps - completedSteps}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-lg">Setup Guide</h3>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Minimize"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-sm font-medium">{completedSteps}/{totalSteps}</span>
        </div>
      </div>

      {/* Critical Requirements Banner */}
      {(!completionData.hasWaiverText || !completionData.hasStripeConnect) && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs text-amber-800">
            <strong>Waiver & Stripe</strong> required before services/bookings
          </span>
        </div>
      )}

      {/* Steps List */}
      <div className="max-h-[350px] overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            Loading...
          </div>
        ) : (
          <div className="p-2">
            {steps.map((step) => {
              const Icon = step.icon;
              const isComplete = step.isComplete === true;
              const isGated = step.isGated;
              const isCritical = step.isCritical;
              const isOptional = step.isOptional;
              const isFinalStep = step.isFinalStep;
              
              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(step)}
                  disabled={isFinalStep}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-all group ${
                    isComplete ? 'bg-green-50' : 
                    isGated ? 'bg-gray-50 opacity-60' :
                    isCritical && !isComplete ? 'bg-amber-50 border border-amber-200' :
                    'hover:bg-gray-50'
                  } ${isFinalStep ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Step Number/Check/Lock */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isComplete 
                        ? 'bg-green-500 text-white' 
                        : isGated
                          ? 'bg-gray-300 text-gray-500'
                          : isOptional
                            ? `${step.color} text-white`
                            : isCritical
                              ? 'bg-amber-500 text-white'
                              : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isComplete ? (
                        <Check className="w-4 h-4" />
                      ) : isGated ? (
                        <Lock className="w-3.5 h-3.5" />
                      ) : (
                        <span className="text-sm font-semibold">{step.id}</span>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium text-sm ${
                          isComplete ? 'text-green-700' : 
                          isGated ? 'text-gray-500' :
                          'text-gray-900'
                        }`}>
                          {step.title}
                        </span>
                        {isComplete && (
                          <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">Done</span>
                        )}
                        {isCritical && !isComplete && (
                          <span className="text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-medium">
                            {step.criticalLabel}
                          </span>
                        )}
                        {isGated && (
                          <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                            Locked
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${isGated ? 'text-gray-400' : 'text-gray-500'}`}>
                        {isGated ? step.gateMessage : step.description}
                      </p>
                    </div>
                    
                    {/* Arrow */}
                    {!isFinalStep && (
                      <ChevronDown className={`w-4 h-4 -rotate-90 transition-colors flex-shrink-0 mt-1 ${
                        isGated ? 'text-gray-300' : 'text-gray-400 group-hover:text-blue-600'
                      }`} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer - All Complete */}
      {completedSteps === totalSteps && (
        <div className="p-3 bg-green-50 border-t border-green-100">
          <div className="flex items-center gap-2 text-green-700">
            <Rocket className="w-4 h-4" />
            <span className="text-sm font-medium">🎉 Setup complete! You're ready to go live!</span>
          </div>
        </div>
      )}

      {/* Help Guide Link */}
      <div className="p-3 bg-gray-50 border-t border-gray-200">
        <button
          onClick={() => {
            // Dispatch event to open help modal
            window.dispatchEvent(new CustomEvent('openHelpGuide', { detail: { section: 'welcome' } }));
          }}
          className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-2"
        >
          <HelpCircle className="w-4 h-4" />
          <span>View all features in Help Guide</span>
        </button>
      </div>
    </div>
  );
};

export default SetupGuideWidget;
